/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable quotes */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Box,
  Typography,
  Tooltip
} from "@mui/material";
import { Environment } from "../../../shared/environment";
import { useSearchParams } from "react-router-dom";
import {
  ProdutosService,
  IListagemDeReferencias,
} from "../../../shared/services/api/estoque/produtos/ProdutosService";

import { DialogAlterarReferencia } from "./DialogAlterarReferencia";
import { v4 as uuidv4 } from "uuid";

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  open: boolean;
  onClose: (value?: string) => void;
}

interface IUpdateref {
  idref: string;
  referencia: string;
  original: boolean;
}

export const DialogReferencia: React.FC<ConfirmationDialogRawProps> = (props) => {
  const { onClose, open, ...other } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<IListagemDeReferencias[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const pagina = useMemo(() => {
    return Number(searchParams.get("pagina") || "1");
  }, [searchParams]);

  const idproduto = useMemo(() => {
    return searchParams.get("idproduto") || props.id;
  }, [searchParams, props.id]);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [tms, setTms] = useState<"warning" | "error" | "info" | "success">("warning");

  const [idref, setIdref] = useState("");
  const [openferaisAlterar, setOpenFeriasAlterar] = useState(false);
  const [dadosFerias, setDadosFerias] = useState<IUpdateref>();

  const handleCloseMsg = (
    _?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return;
    setOpenmsg(false);
  };

  const carregarDados = () => {
    setIsLoading(true);

    ProdutosService.getAllReferencia(pagina, props.id).then((result) => {
      setIsLoading(false);

      if (result instanceof Error) {
        setTms("error");
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setTotalCount(result.totalCount);
        setRows(result.data);
      }
    });
  };

  const handleCloseFeriasAlterar = () => {
    setOpenFeriasAlterar(false);
    if (idproduto !== "novo") carregarDados();
  };

  const handleDelete = (id: string) => {
    ProdutosService.deletereferencia(id).then((result) => {
      if (result instanceof Error) {
        setTms("error");
        setMsg(result.message);
      } else {
        setTms("success");
        setMsg("Referência excluída com sucesso!");
        carregarDados();
      }
      setOpenmsg(true);
    });
  };

  const handleAddReference = () => {
    setDadosFerias(undefined);
    setIdref("novo");
    setOpenFeriasAlterar(true);
  };

  useEffect(() => {
    if (idproduto !== "novo") carregarDados();
  }, [pagina, idproduto, open]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm" {...other}>

      {/* SNACKBAR */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 1500}
        onClose={handleCloseMsg}
      >
        <Alert severity={tms} variant="filled">
          {msg}
        </Alert>
      </Snackbar>

      {/* HEADER */}
      <DialogTitle
        sx={{
          bgcolor: "#ffffff",
          borderBottom: "1px solid #e0e0e0",
          py: 2,
          px: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Referências Compatíveis
        </Typography>

        <Tooltip title="Adicionar nova referência">
          <IconButton color="primary" onClick={handleAddReference}>
            <Icon>add</Icon>
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {/* CONTEÚDO */}
      <DialogContent>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            mt: 1,
            borderRadius: 2,
            maxHeight: 350
          }}
        >
          <Table size="small" stickyHeader>

            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell><b>Referência</b></TableCell>
                <TableCell align="center"><b>Original</b></TableCell>
                <TableCell align="center" width={120}><b>Ações</b></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.idref}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell>{row.referencia}</TableCell>

                  {/* ⭐ ORIGINAL */}
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.3,
                        borderRadius: 1,
                        display: "inline-block",
                        backgroundColor: row.original ? "#e8f5e9" : "#f5f5f5",
                        color: row.original ? "#2e7d32" : "#616161"
                      }}
                    >
                      {row.original ? "Original" : "Similar"}
                    </Typography>
                  </TableCell>

                  {/* AÇÕES */}
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setDadosFerias({
                              idref: row.idref,
                              referencia: row.referencia,
                              original: row.original,
                            });
                            setIdref(row.idref);
                            setOpenFeriasAlterar(true);
                          }}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.idref)}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {/* VAZIO */}
            {totalCount === 0 && !isLoading && (
              <caption>{Environment.LISTAGEM_VAZIA}</caption>
            )}

            {/* FOOTER */}
            <TableFooter>

              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}

              {totalCount > 5 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Pagination
                      page={pagina}
                      count={Math.ceil(totalCount / 5)}
                      onChange={(_, newPage) =>
                        setSearchParams(
                          { idproduto, pagina: newPage.toString() },
                          { replace: true }
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              )}

            </TableFooter>

          </Table>
        </TableContainer>

      </DialogContent>

      {/* ACTIONS */}
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onClose()}
        >
          Voltar
        </Button>
      </DialogActions>

      {/* MODAL */}
      <DialogAlterarReferencia
        id={idproduto}
        value={uuidv4()}
        dados={dadosFerias}
        idref={idref}
        open={openferaisAlterar}
        onClose={handleCloseFeriasAlterar}
      />

    </Dialog>
  );
};